import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import { DESKTOP_MIN_WIDTH } from 'constants';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import React from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import styled from 'styled-components';
import {
  paragraph500Mixin,
  paragraphMixin,
  scrollbarThumbMixin,
  textBreakLineAllMixin,
} from 'styles';

const DropdownCommon = ({
  value = '',
  placeholder = '',
  keyError = '',
  isShowDropdown = false,
  onToggle = () => {},
  onClick = () => {},
  isDisabled = false,
  isHideArrowDownIcon = false,
  // options for Dropdown
  classNameDropdown = '',
  styleDropdown = {},
  // options for DropdownToggle
  classNameDropdownToggle = '',
  styleDropdownToggle = {},
  // Optional test handle for the toggle. Undefined by default, so React omits
  // the attribute entirely for every caller that does not pass one.
  dataTestIdDropdownToggle = undefined,
  renderDropdownToggleChildren = () => null,
  // options for DropdownMenu
  classNameDropdownMenu = '',
  styleDropdownMenu = {},
  renderDropdownMenuChildren = () => null,
  styleDropdownError = {},
}) => {
  const { t } = useLang();

  const isShowArrowDownIcon = !isDisabled && !isHideArrowDownIcon;

  const renderDropdownToggle = () => {
    const renderDropdownToggleChildrenDefault = () => (
      <>
        {value || t(placeholder)}
        {isShowArrowDownIcon && (
          <ArrowDownIcon
            color="var(--ds-c-blue-hyperlink-default)"
            size={{ width: 12, height: 10 }}
            styles={{
              transform: isShowDropdown && 'rotate(180deg)',
            }}
          />
        )}
      </>
    );
    return (
      <DropdownToggleStyled
        data-testid={dataTestIdDropdownToggle}
        className={classNameDropdownToggle}
        style={styleDropdownToggle}
        $isPlaceholder={!value}
        $isDisabled={isDisabled}
        $isShowDropdown={isShowDropdown}
        $isShowArrowDownIcon={isShowArrowDownIcon}
      >
        {renderDropdownToggleChildren() ||
          renderDropdownToggleChildrenDefault()}
      </DropdownToggleStyled>
    );
  };
  const renderDropdownMenu = () => {
    if (!isShowDropdown) return null;
    return (
      <DropdownMenuStyled
        className={classNameDropdownMenu}
        style={styleDropdownMenu}
      >
        {renderDropdownMenuChildren()}
      </DropdownMenuStyled>
    );
  };
  const renderDropdownError = () => {
    if (isEmpty(keyError)) return null;
    return (
      <DropdownErrorStyled style={styleDropdownError}>
        {t(keyError)}
      </DropdownErrorStyled>
    );
  };

  return (
    <DropdownCommonStyled>
      <DropdownStyled
        className={classNameDropdown}
        style={styleDropdown}
        onToggle={onToggle}
        onClick={onClick}
        $isInvalid={keyError}
        $isShowDropdown={isShowDropdown}
        $isDisabled={isDisabled}
      >
        {renderDropdownToggle()}
        {renderDropdownMenu()}
      </DropdownStyled>
      {renderDropdownError()}
    </DropdownCommonStyled>
  );
};

const DropdownCommonStyled = styled.div``;

export const DropdownStyled = styled(Dropdown)`
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 44px;
  height: fit-content;
  border-radius: 8px;
  background: ${(props) =>
    props.$isDisabled ? 'var(--ds-c-grey-disabled)' : 'transparent'};
  border: 1px solid var(--ds-c-grey-light);
  border: ${(props) => props.$isInvalid && '1px solid var(--ds-c-red)'};
  border: ${(props) => props.$isShowDropdown && '1px solid var(--c-primary)'};
  border: ${(props) => props.$isDisabled && 'none'};
`;
export const DropdownToggleStyled = styled(Dropdown.Toggle)`
  ${paragraph500Mixin};
  text-transform: uppercase;
  text-align: left;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border-radius: 12px;
  padding: 8px 10px;
  padding-right: ${(props) => (props?.$isShowArrowDownIcon ? '32px' : '10px')};
  width: 100%;
  min-height: 44px;
  height: fit-content;
  background: transparent !important;
  background-color: transparent !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  cursor: ${(props) => props.$isDisabled && 'not-allowed!important'};
  color: ${(props) =>
    props.$isPlaceholder
      ? 'var(--ds-c-blue-disabled)'
      : 'var(--c-primary)!important'};
  ${(props) => props.style?.color && `color: ${props.style?.color}`};
  color: ${(props) =>
    props.$isDisabled && 'var(--ds-c-grey-neutral)!important'};
  ${textBreakLineAllMixin};
  text-align: left;

  &:hover {
    color: ${(props) =>
      props.$isPlaceholder
        ? 'var(--ds-c-grey-hover)'
        : 'var(--c-primary-hover)'};
    ${(props) =>
      props.style?.colorHover && `color: ${props.style?.colorHover}`};
    color: ${(props) =>
      props.$isDisabled && 'var(--ds-c-grey-neutral)!important'};
  }

  &::after {
    display: none;
  }
  & svg {
    position: absolute;
    right: 12px;
    filter: ${(props) => props?.style?.svgFilter};
    &:hover {
      transform: ${(props) => props?.style?.svgTransformHover};
    }
  }

  ${scrollbarThumbMixin};
`;
export const DropdownErrorStyled = styled.span`
  ${paragraphMixin};
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: var(--ds-c-red);

  position: absolute;
  margin-top: 4px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;
export const DropdownMenuStyled = styled(Dropdown.Menu)`
  padding: 0;
  border: none;
  width: calc(100% + 2px);
  min-height: 44px;
  height: fit-content;
  background: transparent;
  box-shadow: var(--ds-bs-4);
  position: absolute;
  inset: 0px auto auto 0px !important;
  border-radius: 12px 12px 0 0;
  z-index: 10;
  transform: ${(props) =>
    props?.style?.transform || 'translate(-1px, 45px)'}!important;
  ${(props) => props?.style?.left && `left: ${props?.style?.left}!important`};
`;
export const DropdownMenuContainer = styled(Dropdown.Menu)`
  padding: 0;
  border: none;
  max-height: 180px;
  border-top: 1px solid var(--ds-c-grey-disabled);
  border-radius: 0;
  width: ${(props) => props?.style?.width || '100%'};
  min-width: ${(props) => props?.style?.minWidth};
  overflow: auto;
  background: var(--ds-c-white);
  margin-top: -1px !important;
  box-shadow: var(--ds-bs-3) !important;
  inset: ${(props) => props?.style?.inset || '0px auto auto 0px'}!important;
  transform: ${(props) =>
    props?.style?.transform || 'translate(0px, 45px)'}!important;
  padding-bottom: 16px;
  z-index: 1;
  ${scrollbarThumbMixin};
`;
export const DropdownItemStyled = styled(Dropdown.Item)`
  ${paragraph500Mixin};
  display: grid;
  align-items: center;
  padding: 8px 10px;
  text-transform: capitalize;
  min-height: 40px;
  height: fit-content;
  white-space: break-spaces;
  background: var(--ds-c-white);
  margin: 0px;
  padding: 8px 10px;

  a:first-child {
    border-radius: 0;
  }

  &:hover {
    background: ${(props) =>
      props.$isEmpty ? 'var(--ds-c-white)' : 'var(--ds-c-grey-light)'};
    cursor: ${(props) => (props.$isEmpty ? 'default' : 'pointer')};
  }

  &:active {
    color: var(--c-primary);
  }

  & p.item-name-country {
    font-weight: 700;
    margin-bottom: 4px;
    text-transform: uppercase;
    display: grid;
    grid-auto-flow: column;
    justify-content: flex-start;
  }
`;

export const DropdownSearchInput = styled.input`
  ${paragraph500Mixin};
  text-transform: uppercase;
  height: 44px;
  border: none;
  border-radius: 12px 12px 0px 0px;
  background: var(--ds-c-white);
  width: 100%;
  min-height: 44px;
  height: fit-content;
  padding: 10px;

  &:focus {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &::placeholder {
    ${paragraph500Mixin};
    color: var(--ds-c-blue-disabled) !important;
    text-transform: initial;
  }
`;

export default DropdownCommon;
